import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/app/hooks.ts';
import {
  fetchDashboardStats,
  fetchUpcomingWebinars,
} from '@/features/dashboard/store/dashboardSlice';
import { StatsSection } from '@/features/dashboard/components/StatsSection';
import { WebinarCard } from '@/features/dashboard/components/WebinarCard';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import CreateWebinarModal from '@/components/common/CreateWebinarModal';
import FreePlanUpsellModal from '@/components/billing/FreePlanUpsellModal';

export default function Dashboard() {
  const { t } = useTranslation(['dashboard', 'common']);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [createWebinarModalOpen, setCreateWebinarModalOpen] = useState(false);

  const authenticatedUser = useAppSelector((state) => state.auth.user);
  const currentWorkspaceId = useAppSelector((state) => state.workspace.currentWorkspace?.id);
  const { data: allWebinars, loading: isWebinarsLoading } = useAppSelector(
    (state) => state.dashboard.webinars,
  );
  const { period: currentStatsPeriod } = useAppSelector((state) => state.dashboard.stats);

  const displayedWebinars = useMemo(() => {
    return allWebinars.filter((webinar) => webinar.status !== 'DRAFT' && webinar.startDate !== '');
  }, [allWebinars]);

  useEffect(() => {
    if (currentWorkspaceId) {
      dispatch(fetchUpcomingWebinars(currentWorkspaceId));
      dispatch(
        fetchDashboardStats({ workspaceId: currentWorkspaceId, period: currentStatsPeriod }),
      );
    }
  }, [currentWorkspaceId, dispatch, currentStatsPeriod]);
  return (
    <div className="space-y-12">
      <h1 className="text-[32px] font-semibold tracking-tight text-dark">
        {t('home.hello')} <span className="text-tertiary">{authenticatedUser?.firstName}</span>
      </h1>

      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-medium text-dark">{t('webinars.sectionTitle')}</h2>
          <Button variant="ghost" className="text-dark" onClick={() => navigate('/webinars')}>
            {t('webinars.seeAll')}
          </Button>
        </div>

        {displayedWebinars.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {displayedWebinars.map((webinar) => (
              <WebinarCard
                key={`${webinar.id}-${webinar.sessionId}`}
                webinar={webinar}
                variant="full"
              />
            ))}
          </div>
        ) : (
          !isWebinarsLoading && (
            <div className="flex flex-col items-center justify-center py-12 bg-neutral-50 rounded-[20px] border border-dashed border-neutral-200 text-center px-4">
              <h3 className="text-xl font-semibold text-secondary mb-2">
                {t('webinars.emptyState.title')}
              </h3>
              <p className="text-sm text-secondary mb-6 max-w-md">
                {t('webinars.emptyState.description')}
              </p>
              <Button onClick={() => setCreateWebinarModalOpen(true)}>
                <Plus className="mr-2" size={20} />
                {t('webinars.emptyState.createButton')}
              </Button>
            </div>
          )
        )}
      </section>

      <StatsSection />

      <CreateWebinarModal open={createWebinarModalOpen} onOpenChange={setCreateWebinarModalOpen} />

      <FreePlanUpsellModal />

      {/*<section className="space-y-4">*/}
      {/*  <h2 className="text-xl font-medium text-dark">Nos conseils et astuces</h2>*/}
      {/*  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">*/}
      {/*    {[1, 2, 3, 4].map((index) => (*/}
      {/*      <div*/}
      {/*        key={index}*/}
      {/*        className="rounded-2xl overflow-hidden border border-neutral-100 group cursor-pointer shadow-sm hover:shadow-md transition-shadow"*/}
      {/*      >*/}
      {/*        <div className="h-40 bg-neutral-200 relative">*/}
      {/*          <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors" />*/}
      {/*        </div>*/}
      {/*        <div className="p-4 bg-white">*/}
      {/*          <h4 className="font-medium text-dark line-clamp-2">*/}
      {/*            Comment optimiser son webinaire ?*/}
      {/*          </h4>*/}
      {/*          <p className="text-xs text-tertiary mt-2">13:23 min</p>*/}
      {/*        </div>*/}
      {/*      </div>*/}
      {/*    ))}*/}
      {/*  </div>*/}
      {/*</section>*/}
    </div>
  );
}
